<template>
  <div class="login-form">
    <div class="login-form__form-wrapper">
      <form class="login-form__form" @submit.prevent="login">
        <div class="login-form__form-group">
          <input
            id="email"
            v-model="email"
            v-validate="'required|email'"
            class="login-form__input"
            :class="{
              error: errors.has('email'),
              filled: (email && email.length) || autofilled,
              valid: fields.email ? fields.email.valid : null
            }"
            type="text"
            name="email"
            :placeholder="$tr('login.placeholders.login')"
            required
            @input="handleInputEmail"
            @animationstart="checkAnimation"
          />

          <div v-if="loginFailed" class="login-form__failed">
            <div>{{ $tr('login.errorEmailPassword') }}</div>
            <div>{{ $tr('login.tryAgain') }}</div>
          </div>
        </div>

        <div class="login-form__form-group">
          <input
            id="password"
            v-model="password"
            v-validate="'required'"
            type="password"
            class="login-form__input"
            :class="{
              error: errors.has('password'),
              filled: (password && password.length) || autofilled,
              valid: fields.password ? fields.password.valid : null
            }"
            name="password"
            :placeholder="$tr('login.placeholders.password')"
            required
            @input="handleInput"
          />

          <div v-tooltip.bottom="$tr('login.forgotPassword')" class="login-form__iforgot">
            <i
              class="icon icon-iforgot"
              @click.prevent="$router.push({ name: 'password-recovery' })"
            ></i>
          </div>
        </div>

        <div v-if="show_captcha" class="login-form__form-group">
          <vue-recaptcha
            :sitekey="sitekey"
            @verify="check_captcha"
            @expired="recaptcha_res = null"
          />
        </div>

        <div class="login-form__form-group">
          <vbutton
            label="login.submit"
            size="bigger"
            block
            :disabled="isSubmitDisabled"
            :loading="loading"
            @click="login"
          />
        </div>

        <div class="login-form__or">
          <div class="login-form__or-text">
            {{ $tr('vacancy.preferences.footerInfo.first.content.or') }}
          </div>
        </div>

        <div class="login-form__services">
          <img
            v-tooltip.bottom="$tr('login.enter-sso')"
            src="/static/images/login/logo-sso.svg"
            alt="sso"
            @click.prevent="$router.push({ name: 'login-sso' })"
          />
        </div>

        <div class="login-form__bottom-text">
          <span>{{ $tr('login.not-registered') }}</span>

          <router-link tag="a" :to="{ name: 'registration' }">
            {{ $tr('vacancy.openRegistration.buttonRegisterModal') }}
          </router-link>
        </div>
      </form>
    </div>

    <popup v-if="is_password_expiry" :show-modal-close="false" @close="is_password_expiry = false">
      <div slot="body" class="text-center">
        <img class="modal-img" width="48" src="/static/images/elements/security.svg" alt="lock" />
        <div>
          {{ $tr('login.passwordUseRestrict') }}
        </div>
      </div>
    </popup>

    <error-modal
      v-if="csrf_token_error"
      :text="csrf_token_error ? csrf_token_error.message : ''"
      @close="close_error_modal"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import VueRecaptcha from 'vue-recaptcha';

import ErrorModal from '@/components/modals/Error';
import Popup from '@/components/modals/Popup';

export default {
  name: 'LoginFormNew',

  components: { VueRecaptcha, ErrorModal, Popup },

  data() {
    return {
      email: '',
      password: '',
      // showPassword: false,
      loginFailed: null,
      sitekey: process.env.recapcha_key,
      recaptcha_res: null,
      disabled: false,
      show_captcha: false,
      csrf_token_error: null,
      error_code: null,
      delay: null,
      loading: false,
      csrf_token_timer: null,
      is_password_expiry: false,
      autofilled: false
    };
  },

  computed: {
    ...mapGetters('services', ['domain']),

    submitButtonEnabled() {
      const fields = Object.keys(this.fields).map(k => this.fields[k]);

      return fields.every(f => f.valid === true);
    },

    isSubmitDisabled() {
      if (this.autofilled) {
        return false;
      }

      return !this.submitButtonEnabled || !this.can_login;
    },

    loginSuccess() {
      return this.$store.state.user.result.loginSuccess;
    },

    pass_captcha() {
      // чекаем капчу только если дошли до этапа чека капчи
      return this.show_captcha ? this.recaptcha_res !== null : true;
    },

    can_login() {
      // проверяем можно ли пускать авторизацию
      return this.disabled === false && this.pass_captcha;
    }
  },

  watch: {
    csrf_token_error(val) {
      if (val) {
        window.location.reload();
      }
    },

    loginSuccess(val) {
      if (!val) {
        this.loginFailed = true;
      }
    }
  },

  created() {
    this.set_domain();
  },

  beforeMount() {
    // reload page after 9 minutes to refresh csrf_token
    const delay = 9 * 60 * 1000;

    this.csrf_token_timer = setTimeout(() => {
      window.location.reload();
    }, delay);
  },

  beforeDestroy() {
    clearTimeout(this.csrf_token_timer);
  },

  methods: {
    ...mapMutations('process', ['setName']),

    ...mapMutations('services', ['set_domain']),

    handleInput() {
      this.autofilled = false;
    },

    handleInputEmail(v) {
      this.autofilled = false;

      if (v && v.target && v.target.value) {
        this.email = v.target.value.trim();
      }
    },

    login() {
      this.disabled = true;
      this.show_captcha = false;
      this.error_code = null;
      this.delay = null;
      let route = this.$route.query.redirect || '/';
      this.loading = true;
      // Hotfix for progressbar
      // this.$nprogress.start();

      this.$store
        .dispatch('user/login', {
          email: this.email,
          password: this.password,
          captcha_token: this.recaptcha_res,
          redirect: {
            path: route
          }
        })
        .then(user => {
          // set Process Name if it's in company property
          if (user.company && user.company.meta && user.company.meta.active_process) {
            if (
              user.company.meta.active_process !== undefined &&
              user.company.meta.active_process !== null
            ) {
              this.setName(user.company.meta.active_process);
            }
          }

          this.$store.commit('user/loginSuccess');
          this.$store.commit('global/clearError');

          this.loading = false;

          if (user && user.redirect_domain) {
            return;
          }

          if (this.yandex_counter !== '' && this.yandex_counter !== undefined) {
            const yandex_email = user ? user.email : '';

            this.yandex_counter.setUserID(yandex_email);
            this.yandex_counter.params({
              registration_s1_c1: false, // запишите 'true' если пользователь прошёл первый шаг регистрации, в противном случае - пусто
              registration_s2: false, // запишите 'true' если пользователь прошёл второй шаг регистрации, в противном случае - пусто
              visitor_type: user ? user.email : '', // запишите этот параметр, если пользователь авторизовался
              company: user && user.company ? user.company.name : '', // запишите название компании, в противном случае - пусто
              company_geo: user && user.country ? user.country.title : '' // запишите регион компании, в противном случае - пусто
            });
          }

          if (this.$ga) {
            this.$ga({
              email: user ? user.email : '',
              company_name: user && user.company ? user.company.name : '',
              geo: user && user.country ? user.country.title : '',
              reg1: 'false',
              reg2: 'false'
            });
          }

          if (window.gtag) {
            window.gtag({ event: 'login' });
          }

          const redirect_path = user && user.email && !user.company ? '/admin' : route;

          this.$router.push({ path: redirect_path });
          this.disabled = false;
        })
        .catch(e => {
          this.loading = false;
          this.recaptcha_res = null;

          if (e.status === 400 || e.status === 429) {
            if (e.code !== undefined) {
              this.error_code = e.code;
            }
            if (e.delay !== undefined) {
              this.delay = e.delay;
            }

            this.$store.commit('user/loginFailure', e);
          } else if (e.response.meta.error.message === 'Your password has expired') {
            this.is_password_expiry = true;
          } else {
            this.$noty.error(this.$tr('common.error.text'));
          }

          this.check_security(e);
        });
    },

    check_captcha(v) {
      this.recaptcha_res = v;
    },

    close_error_modal() {
      window.location.reload(false);
    },

    check_security(e) {
      if (this.error_code === 'captcha_required') {
        this.show_captcha = true;
      }
      if (this.error_code === 'csrf_token_invalid') {
        this.csrf_token_error = e;
      }
      if (this.delay !== null) {
        setTimeout(() => {
          this.disabled = false;
        }, this.delay * 1000);
      } else {
        this.disabled = false;
      }
    },

    checkAnimation(e) {
      if (e.animationName == 'onAutoFillStart') {
        this.autofilled = true;
      }
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/fonts.scss';

.login-form {
  display: flex;
  align-items: center;
  flex-direction: column;

  &__form {
    position: relative;
    max-width: 300px;
    padding: 20px;

    @media screen and (orientation: landscape) and (max-height: 450px) {
      padding: 10px;
    }
  }

  &__form-group {
    margin-bottom: 20px;
    position: relative;
  }

  &__input {
    border: 1px solid $grey;
    transition: border 0.3s;
    height: 42px;
    width: 100%;

    &.error {
      border: 1px solid $red;
      transition: border 0.3s;
    }

    &.valid {
      border: 1px solid $green;
      transition: border 0.3s;
    }
  }

  &__iforgot {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 22px;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    i {
      color: $grey;
      transition: color 0.3s;
    }

    &:hover {
      i {
        color: $green-hover;
        transition: color 0.3s;
      }
    }
  }

  &__or {
    background: $grey-light;
    width: 100%;
    height: 1px;
    margin: 1.875em auto;
    position: relative;

    @media screen and (orientation: landscape) and (max-height: 450px) {
      margin: 0.95em auto;
    }
  }

  &__or-text {
    position: absolute;
    top: 50%;
    left: 50%;
    color: $grey-light;
    font-size: 0.75em;
    transform: translate(-50%, -55%);
    background: #fff;
    padding: 0.19em 0.5em;
  }

  &__services {
    display: flex;
    justify-content: center;
    margin-bottom: 1.25em;

    > img {
      width: 1.75em;
      cursor: pointer;

      &:hover {
        box-shadow: $box-shadow;
      }
    }
  }

  &__bottom-text {
    font-size: 0.75em;
    text-align: center;

    > span {
      color: $main-dark;
    }

    > a {
      color: $green;
      text-decoration: underline;
      transition: color 0.3s;

      &:hover {
        color: $green-hover;
        transition: color 0.3s;
      }
    }
  }

  &__failed {
    position: absolute;
    right: -20px;
    top: -22px;
    width: 200px;
    transform: translateX(100%);
    height: 84px;
    background: $main-dark;
    color: #fff;
    font-size: 12px;
    padding: 10px;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    box-shadow: $box-shadow;

    > div:last-child {
      margin-top: 6px;
    }

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      transform: translateX(-10px);
      border-top: 42px solid transparent;
      border-bottom: 42px solid transparent;
      border-right: 10px solid $main-dark;
    }
  }

  &__title {
    @include mixin-text-header;
    text-align: center;
    padding: 0 20px;
  }

  &__disclaimer {
    font-style: italic;
    font-weight: 300;
    font-size: 12px;
    margin-bottom: 15px;
    color: $grey;

    a {
      text-decoration: underline;
      color: $grey;
      transition: color 0.3s;

      &:hover {
        color: $green;
        transition: color 0.3s;
      }
    }
  }
}
</style>
